import { useRef, useState } from "react";
import { CalendarDays, ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDate } from "@/lib/api";
import { Calendar, useRangePick } from "@/components/Calendar";
import { cn } from "@/lib/utils";

const iso = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

function presetRange(key) {
  const now = new Date();
  const y = now.getFullYear();
  const days = (n) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + n);
  switch (key) {
    case "30d":
      return [iso(days(-30)), iso(now)];
    case "90d":
      return [iso(days(-90)), iso(now)];
    case "ytd":
      return [`${y}-01-01`, `${y}-12-31`];
    case "last_year":
      return [`${y - 1}-01-01`, `${y - 1}-12-31`];
    case "next90":
      return [iso(now), iso(days(90))];
    case "upcoming":
      return [iso(days(1)), ""];
    default:
      return ["", ""];
  }
}

export const PRESETS = [
  { key: "any", label: "Any date" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "ytd", label: "This year" },
  { key: "last_year", label: "Last year" },
  { key: "next90", label: "Next 90 days" },
  { key: "upcoming", label: "Upcoming" },
];

export const EMPTY_RANGE = { preset: "any", from: "", to: "", field: "release_date" };

/** Turns the filter's value into API params. */
export function rangeParams(v) {
  if (!v) return {};
  const out = {};
  if (v.field) out.date_field = v.field;
  if (v.from) out.date_from = v.from;
  if (v.to) out.date_to = v.to;
  return out;
}

export function rangeActive(v) {
  return !!(v && (v.from || v.to));
}

/**
 * A date filter: quick presets, or a custom from/to, plus (optionally) which
 * date to filter on — release date, original release date or when it was added.
 */
export default function DateRangeFilter({ value = EMPTY_RANGE, onChange, fields, className }) {
  const [open, setOpen] = useState(false);
  const [align, setAlign] = useState("start");
  const trigger = useRef(null);
  const active = rangeActive(value);

  // Open towards whichever side has room, so the panel never jams into the
  // corner of the screen when the button sits on the right of the page.
  const onOpenChange = (v) => {
    if (v && trigger.current) {
      const r = trigger.current.getBoundingClientRect();
      setAlign(r.left + 560 > window.innerWidth && r.right > 560 ? "end" : "start");
    }
    setOpen(v);
  };
  const preset = PRESETS.find((p) => p.key === value.preset);
  const label = !active
    ? "Any date"
    : value.preset && value.preset !== "custom" && preset
    ? preset.label
    : `${value.from ? formatDate(value.from) : "…"} – ${value.to ? formatDate(value.to) : "…"}`;
  const fieldLabel = fields?.find((f) => f.value === value.field)?.label;

  const range = useRangePick(value, (v) => {
    onChange(v);
    setOpen(false);
  });

  const pick = (key) => {
    const [from, to] = presetRange(key);
    onChange({ ...value, preset: key, from, to });
    if (key !== "custom") setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={trigger}
          type="button"
          className={cn(
            "inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-[13px] shrink-0 transition-colors",
            active ? "border-brand bg-brand-light text-brand font-medium" : "border-line bg-card text-ink hover:border-brand-ring",
            className
          )}
        >
          <CalendarDays className="w-4 h-4" />
          <span className="whitespace-nowrap">
            {fields && active && fieldLabel ? `${fieldLabel}: ` : ""}
            {label}
          </span>
          {active ? (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear date filter"
              onClick={(e) => {
                e.stopPropagation();
                onChange({ ...value, preset: "any", from: "", to: "" });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  onChange({ ...value, preset: "any", from: "", to: "" });
                }
              }}
              className="-mr-1 p-0.5 rounded hover:bg-brand/10"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-muted2" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} sideOffset={8} collisionPadding={16} className="w-auto max-w-[calc(100vw-32px)] p-0 rounded-2xl shadow-lift">
        <div className="flex flex-col sm:flex-row">
          <div className="sm:w-[190px] p-3 border-b sm:border-b-0 sm:border-r border-line">
            {fields && (
              <div className="mb-3">
                <div className="eyebrow mb-2">Filter on</div>
                <div className="flex flex-wrap gap-1.5">
                  {fields.map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => onChange({ ...value, field: f.value })}
                      className={cn(
                        "px-2.5 h-7 rounded-full text-[12px] font-medium border",
                        value.field === f.value ? "bg-brand text-white border-brand" : "border-line text-ink hover:border-brand-ring"
                      )}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="eyebrow mb-1.5">Quick pick</div>
            <div className="grid grid-cols-2 sm:grid-cols-1 gap-0.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => pick(p.key)}
                  className={cn(
                    "text-left px-2.5 py-1.5 rounded-lg text-[13px]",
                    (value.preset || "any") === p.key ? "bg-brand-light text-brand font-medium" : "hover:bg-brand-light/50"
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className={cn("rounded-lg border px-3 py-1.5", range.from && !range.to ? "border-brand" : "border-line")}>
                <div className="eyebrow text-[9.5px]">From</div>
                <div className="text-[13px] mt-0.5">{range.from ? formatDate(range.from) : <span className="text-muted2">Pick a day</span>}</div>
              </div>
              <div className={cn("rounded-lg border px-3 py-1.5", range.from && !range.to ? "border-brand-ring bg-brand-light/40" : "border-line")}>
                <div className="eyebrow text-[9.5px]">To</div>
                <div className="text-[13px] mt-0.5">{range.to ? formatDate(range.to) : <span className="text-muted2">{range.from ? "Now pick the end" : "—"}</span>}</div>
              </div>
            </div>
            <Calendar mode="range" from={range.from} to={range.to} onPick={range.pick} />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
